import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { usePageTitle } from '@/hooks/usePageTitle';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { Form, FormField } from '@/components/ui/form';
import { ApiErrorSummary } from '@/components/ApiErrorSummary';
import { getApiErrorDetails, type ApiErrorDetails } from '@/lib/getApiErrorMessage';
import { useAuthStore } from '@/features/authentication/store/authStore';
import { authService } from '@features/authentication/services/authService';
import {
  selfChangePasswordSchema,
  type SelfChangePasswordFormData,
} from '@features/users/schemas/userFormSchema';

export function ForceChangePasswordPage() {
  usePageTitle('Change Password');
  const navigate = useNavigate();
  const { logout, clearMustChangePassword } = useAuthStore();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<ApiErrorDetails | null>(null);

  const form = useForm<SelfChangePasswordFormData>({
    resolver: zodResolver(selfChangePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const handleSubmit = async (data: SelfChangePasswordFormData) => {
    setSubmitError(null);
    try {
      await authService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword,
      });
      clearMustChangePassword();
      toast.success('Password changed successfully');
      // After password change, tokens are revoked — need to re-login
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      const errorDetails = getApiErrorDetails(err, 'Failed to change password');
      setSubmitError(errorDetails);
      toast.error(errorDetails.message);
    }
  };

  // Cardless (86d3cfxyp) — matches the restyled login; renders in the same
  // AuthLayout form column beside the brand illustration.
  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-warning" />
          <h2 className="text-xl font-semibold text-foreground">
            Change Your Password
          </h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          You are using a temporary password. Please set a new password to
          continue.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <ApiErrorSummary error={submitError} />

          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field, fieldState }) => (
              <div>
                <FloatingLabelInput
                  id="currentPassword"
                  label="Temporary Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={!!fieldState.error}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                      aria-label={
                        showCurrentPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                  {...field}
                />
                {fieldState.error && (
                  <p className="mt-1.5 text-xs text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <FormField
            control={form.control}
            name="newPassword"
            render={({ field, fieldState }) => (
              <div>
                <FloatingLabelInput
                  id="newPassword"
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  error={!!fieldState.error}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                      aria-label={
                        showNewPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                  {...field}
                />
                {fieldState.error ? (
                  <p className="mt-1.5 text-xs text-destructive">
                    {fieldState.error.message}
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Min 8 chars with uppercase, lowercase, number, and special
                    character
                  </p>
                )}
              </div>
            )}
          />

          <FormField
            control={form.control}
            name="confirmNewPassword"
            render={({ field, fieldState }) => (
              <div>
                <FloatingLabelInput
                  id="confirmNewPassword"
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  error={!!fieldState.error}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                      aria-label={
                        showConfirmPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                  {...field}
                />
                {fieldState.error && (
                  <p className="mt-1.5 text-xs text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <Button
            type="submit"
            className="w-full"
            isLoading={form.formState.isSubmitting}
          >
            Set New Password
          </Button>
        </form>
      </Form>
    </div>
  );
}
